import React, { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import { LEGAL_URLS } from '@/config/legal';

interface GuestPublicConsentModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onSignUp: () => void;
  onSignIn: () => void;
}

export default function GuestPublicConsentModal({
  isOpen,
  onAccept,
  onSignUp,
  onSignIn,
}: GuestPublicConsentModalProps) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAccepted(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div className="relative bg-bg-panel rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-border">
        <div className="p-6 md:p-8">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
            <Globe className="w-6 h-6 text-accent" strokeWidth={2} />
          </div>

          <h2 className="text-xl font-semibold text-text-primary text-center mb-3">
            {t('guestConsent.title')}
          </h2>

          <p className="text-sm leading-relaxed text-text-secondary text-center mb-4">
            {t('guestConsent.message')}
          </p>

          <p className="text-sm leading-relaxed text-text-secondary text-center mb-6">
            {t('guestConsent.privateHint')}
          </p>

          <label className="flex items-start gap-3 rounded-xl border border-border bg-bg-control px-4 py-3 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border bg-bg-panel checked:bg-accent shrink-0"
            />
            <span className="text-xs leading-relaxed text-text-secondary">
              {t('guestConsent.acceptPrefix')}{' '}
              <a
                href={LEGAL_URLS.terms}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                {t('auth.terms')}
              </a>
              .
            </span>
          </label>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={onAccept}
              disabled={!accepted}
              className="w-full px-4 py-2.5 bg-accent text-bg-main rounded-full text-sm font-semibold hover:bg-accent-hover transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('guestConsent.continue')}
            </button>
            <button
              type="button"
              onClick={onSignUp}
              className="w-full px-4 py-2.5 bg-bg-control text-text-primary rounded-full text-sm font-medium hover:bg-bg-control-hover transition-all border border-border"
            >
              {t('auth.createAccount')}
            </button>
            <button
              type="button"
              onClick={onSignIn}
              className="w-full px-4 py-2.5 text-text-secondary rounded-full text-sm font-medium hover:text-text-primary transition-all"
            >
              {t('auth.signIn')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
